import React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider as ReduxProvider } from 'react-redux';
import $ from 'jquery';
import { Router } from 'react-router-dom';
import { history } from 'app/common/history/history';
import { createBrowserHistory as createHistory } from 'history';
import { QueryClientProvider } from 'react-query';
import { queryClient } from 'app/common/api/query';

const usageStatsHistory = createHistory({
    basename: '/usagestats'
});

const REACTDOM_WRAPPER_CONTAINER_CLASS = 'react-dom-wrapper-component-container';

/**
 * Wrap the given element with the given provider component
 * @param element       - The element to wrap with the provider
 * @param providers     - Array of providers to wrap the element with. A provider must be one of these strings:
 *      - 'redux': wrap with a react-redux provider that provides requestedStore
 *      - 'router': wrap with a react-router provider that provides routing info for newsdesk
 *      - 'usageStatsRouter': wrap with a react-router provider that provides routing info for usage stats
 *      - an Error is thrown if the provider is any other value
 * @param requestedStore - The requested store (if redux was one of the providers, null otherwise)
 * @returns {element} a wrapped element
 */
function wrapElementWithProviders(element, providers, requestedStore = null) {
    return providers.reduce((element, provider) => {
        switch (provider) {
            case 'redux':
                if (!requestedStore) {
                    throw new Error('For "redux" provider you must pass in the store');
                }
                return <ReduxProvider store={requestedStore}>{element}</ReduxProvider>;
            case 'router':
                return <Router history={history}>{element}</Router>;
            case 'usageStatsRouter':
                return <Router history={usageStatsHistory}>{element}</Router>;
            case 'query':
                return <QueryClientProvider client={queryClient}>{element}</QueryClientProvider>;
            default:
                throw new Error(`Passed in provider string ${provider} not recognized!`);
        }
    }, element);
}

/**
 * Renders a react element into the dom using ReactDOM.render(), but also wrapping with the necessary providers.  This function is required
 * instead of ReactDOM.render() when an element needs a top-level provider but it's not rendered as a descendant of the top-level App.js
 * component which includes its own providers.
 * @param element the element to render into the dom
 * @param container see renderComponentWithProviders jsdoc
 * @param providers see renderComponentWithProviders jsdoc
 * @param requestedStore see renderComponentWithProviders jsdoc
 */
function renderElementWithProviders(element, container, providers = [], requestedStore) {
    let wrappedElement = wrapElementWithProviders(element, providers, requestedStore);
    $(container).addClass(REACTDOM_WRAPPER_CONTAINER_CLASS);
    return ReactDOM.render(wrappedElement, container);
}

/**
 * Renders a react component into the dom using ReactDOM.render(), but also wrapping with the necessary providers.  This function is required
 * instead of ReactDOM.render() when a component needs a top-level provider but it's not rendered as a descendant of the top-level App.js
 * component which includes its own providers.
 * @param Component the component to render into the dom
 * @param container the DOM container to render an instance of the component into
 * @param props the props that will be passed to the component
 * @param providers an array of strings indicating which providers should be included.  The element is wrapped in the order of this array, with
 *   inner-most wrapper being the first provider in the array and the outer-most wrapper being the last provider in the array, etc.  Each
 *   provider in this array must be a string, and match one of the values mentioned on wrapElementWithProvider().    };
 * @param requestedStore if 'redux' is one of the providers the requested stored needs to be passed in
 *   DOES NOT SUPPORT MULTIPLE REDUX STORE PROVIDERS
 */
function renderComponentWithProviders(Component, container, props = {}, providers = [], requestedStore) {
    let element = <Component {...props} />;
    return renderElementWithProviders(element, container, providers, requestedStore);
}

/**
 * Utility to cleanup all React components from specific part of the DOM tree
 *
 * @param $domContext - where to look for react components (for example: this.$el - in case of a Backbone.View)
 */
function removeComponentsFromContext($domContext) {
    let reactClass = `.${REACTDOM_WRAPPER_CONTAINER_CLASS}`;
    let $reactNodes = $domContext.find(reactClass).addBack(reactClass);

    $reactNodes.each(function (index, element) {
        ReactDOM.unmountComponentAtNode(element);
    });
}

export { removeComponentsFromContext, renderComponentWithProviders, renderElementWithProviders };
